import folderNameToId from "owa-session-store/lib/utils/folderNameToId";

import getPrimaryFolderTreeRootFolder from "../selectors/getPrimaryFolderTreeRootFolder";
import getFolderTable from "../selectors/getFolderTable";
import getArchiveFolderTreeRootFolder from "../selectors/getArchiveFolderTreeRootFolder";
import type { MailFolder, MailboxType } from "owa-graph-schema";
import type DistinguishedFolderIdName from "owa-service/lib/contract/DistinguishedFolderIdName";
import getSharedFolderRoot from "../selectors/getSharedFolderRoot";
import { isSameMailboxInfo } from "owa-account-source-list-store";
import { STICKY_NOTE_FOLDER_CLASS } from "owa-folders-constants";
import { isGlobalFeatureEnabled } from "owa-feature-flags";

import { isSameCoprincipalAccountMailboxInfos, type MailboxInfo } from "owa-client-types";

export function getFolderByDistinguishedId(distinguishedId: DistinguishedFolderIdName, mailboxInfo: MailboxInfo): MailFolder | undefined {
    const folderTable = getFolderTable();
    const folderId = folderNameToId(distinguishedId, mailboxInfo);
    return folderId ? folderTable.get(folderId) : undefined;
}

/**
 * get root folder of mailbox
 * @return root MailFolder
 */
export function getRootFolderForMailboxInfo(mailboxInfo: MailboxInfo): MailFolder | undefined {
    // If someone wants to get child folders of a shared folder root,
    // then it must pass mailboxSmtpAddress OR throw error.
    if (mailboxInfo.type === "SharedMailbox" && !mailboxInfo.mailboxSmtpAddress) {
        throw new Error("To get all the shared folders you must pass principalSMTPAddress");
    }
    
    switch (mailboxInfo.type) {
        case "ArchiveMailbox":
            return getArchiveFolderTreeRootFolder(mailboxInfo);
        case "SharedMailbox":
            return getSharedFolderRoot(mailboxInfo.mailboxSmtpAddress, mailboxInfo);
        default:
            return getPrimaryFolderTreeRootFolder(mailboxInfo);
    }
}

/**
 * Function calculates and compares if the mailboxType of a given folder id is same as given mailboxType
 * @param mailboxType - mailboxType which needs to be checked
 * @param folderId - folderId
 */
export function isFolderInMailboxType(folderId: string, mailboxType: MailboxType): boolean {
    return getFolderMailboxInfo(folderId)?.type === mailboxType;
}

/**
 * Returns true when the given folder is the non-default notes (sticky note) folder synced for
 * an Archive mailbox, and the fp-archive-notes-folder flight is enabled. Mail item actions
 * (reply, forward, delete, etc.) are not supported for this folder, so this is the single
 * source of truth callers should use to decide whether to hide/disable those actions.
 *
 * The primary mailbox's own distinguished Notes folder is intentionally excluded even though it
 * shares the same FolderClass, since mail item actions continue to be fully supported there.
 */
export function isArchiveNotesFolder(folderId: string | null | undefined): boolean {
    if (!folderId) {
        return false;
    }
    return (isGlobalFeatureEnabled("fp-archive-notes-folder") &&
        getFolderTable().get(folderId)?.FolderClass === STICKY_NOTE_FOLDER_CLASS &&
        isFolderInMailboxType(folderId, "ArchiveMailbox"));
}

/**
 * Function compares the accounts of 2 folders
 * @param folderId1 - folderId of first folder
 * @param folderId2 - folderId of second folder
 */
export function areFoldersInSameAccount(folderId1: string, folderId2: string): boolean {
    return isSameCoprincipalAccountMailboxInfos(getFolderMailboxInfo(folderId1), getFolderMailboxInfo(folderId2));
}

/**
 * Function compares the mailbox of 2 folders
 * @param folderId1 - folderId of first folder
 * @param folderId2 - folderId of second folder
 */
export function areFoldersInSameMailbox(folderId1: string, folderId2: string): boolean {
    const folder1MailboxInfo = getFolderMailboxInfo(folderId1);
    const folder2MailboxInfo = getFolderMailboxInfo(folderId2);
    
    // Same mailbox is local operation
    if (folder1MailboxInfo &&
        folder2MailboxInfo &&
        isSameMailboxInfo(folder1MailboxInfo, folder2MailboxInfo)) {
        // This is to handle Archive to Archive, since it can have an Archive->AuxArchive (or vice-versa) configuration
        if (folder1MailboxInfo.type === "ArchiveMailbox" &&
            folder2MailboxInfo.type === "ArchiveMailbox" &&
            folder1MailboxInfo.auxiliaryMailboxGuid !== folder2MailboxInfo.auxiliaryMailboxGuid) {
            // If the MailboxGuid are different, then it is not on the same mailbox
            return false;
        }
        
        return true;
    }
    
    return false;
}

function getFolderMailboxInfo(folderId: string): MailboxInfo | undefined {
    return getFolderTable().get(folderId)?.mailboxInfo;
}
